import React from 'react';
import { motion } from 'framer-motion';
import { Camera, Upload, Brain, Bell, Eye } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const HowItWorks = () => {
  const { t } = useLanguage();

  const steps = t('howItWorks.steps');
  const icons = [Camera, Upload, Brain, Bell, Eye];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  return (
    <section 
      id="how-it-works" 
      data-testid="how-it-works-section"
      className="py-24 md:py-32 relative overflow-hidden"
    >
      {/* Background Glow */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#FF7A00]/5 rounded-full blur-[150px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 md:mb-24"
        >
          <span className="inline-block px-4 py-2 rounded-full bg-[#FF7A00]/10 text-[#FF7A00] text-sm tracking-widest uppercase mb-4">
            Process
          </span>
          <h2 className="text-4xl md:text-6xl font-bold tracking-tight mb-4">
            {t('howItWorks.title')}
          </h2>
          <p className="text-lg text-white/60 max-w-xl mx-auto">
            {t('howItWorks.subtitle')}
          </p>
        </motion.div>

        {/* Steps Timeline */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="relative"
        >
          {/* Connection Line - Desktop */}
          <div className="hidden lg:block absolute top-24 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-[#FF7A00]/30 to-transparent" />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 lg:gap-4">
            {steps.map((step, index) => {
              const Icon = icons[index];
              return (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  data-testid={`step-${index}`}
                  className="relative group"
                >
                  {/* Step Card */}
                  <div className="glass-card rounded-2xl p-6 h-full flex flex-col items-center text-center">
                    {/* Step Number & Icon */}
                    <div className="relative mb-6">
                      {/* Glow effect on hover */}
                      <div className="absolute inset-0 bg-[#FF7A00]/20 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                      
                      {/* Icon Container */}
                      <div className="relative w-16 h-16 rounded-full bg-gradient-to-br from-[#FF7A00]/20 to-transparent border border-[#FF7A00]/30 flex items-center justify-center group-hover:border-[#FF7A00] transition-colors duration-300">
                        <Icon className="w-7 h-7 text-[#FF7A00]" />
                      </div>
                      
                      {/* Step Number Badge */}
                      <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-[#FF7A00] flex items-center justify-center text-xs font-bold text-white">
                        {step.number}
                      </div>
                    </div>

                    {/* Content */}
                    <h3 className="text-xl font-semibold mb-3 text-white group-hover:text-[#FF7A00] transition-colors">
                      {step.title}
                    </h3>
                    <p className="text-sm text-white/50 leading-relaxed">
                      {step.description}
                    </p>
                  </div>

                  {/* Arrow connector - between cards on desktop */}
                  {index < steps.length - 1 && (
                    <div className="hidden lg:flex absolute top-24 -right-4 w-8 items-center justify-center z-10">
                      <div className="w-3 h-3 border-t-2 border-r-2 border-[#FF7A00]/50 rotate-45" />
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* Bottom Feature Highlight */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-20 text-center"
        >
          <div className="inline-flex items-center gap-4 px-6 py-4 rounded-2xl bg-white/5 border border-white/10">
            <div className="flex items-center gap-2">
              <Brain className="w-5 h-5 text-[#FF7A00]" />
              <span className="text-white/70 text-sm">Powered by</span>
            </div>
            <span className="text-white font-semibold">Amazon Rekognition</span>
            <span className="px-3 py-1 rounded-full bg-[#FF7A00]/10 text-[#FF7A00] text-xs font-medium">
              AI
            </span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default HowItWorks;
